import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Target, Eye, Heart, Users, Award, Lightbulb, ArrowRight } from 'lucide-react';

const Empresa = () => {
  const values = [
    {
      icon: Lightbulb,
      title: 'Innovación',
      description: 'Adoptamos las últimas tecnologías solares para ofrecer las soluciones más eficientes del mercado.'
    },
    {
      icon: Heart,
      title: 'Compromiso',
      description: 'Comprometidos con el planeta y con el bienestar de nuestros clientes, ofreciendo un servicio cercano y honesto.'
    },
    {
      icon: Award,
      title: 'Calidad',
      description: 'Trabajamos solo con materiales premium y técnicos certificados para garantizar instalaciones de máxima calidad.'
    },
    {
      icon: Users,
      title: 'Cercanía',
      description: 'Te acompañamos en cada paso del proceso, desde el primer contacto hasta años después de la instalación.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Sobre Nubia Solar - Empresa de Energía Solar en Madrid, Valencia y Extremadura</title>
        <meta name="description" content="Líderes en instalación de placas solares en Madrid, Valencia y Extremadura. Más de 1,000 instalaciones exitosas. Profesionales certificados y garantía total." />
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-nubia-green-primary mb-6">
              Sobre Nubia Solar
            </h1>
            <p className="text-xl text-nubia-text-dark/70 leading-relaxed">
              Líderes en energía solar con más de 1,000 instalaciones exitosas en toda España
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-6">
                  Quiénes Somos
                </h2>
                <div className="space-y-4 text-lg text-nubia-text-dark/70 leading-relaxed">
                  <p>
                    Nubia Solar es una empresa española especializada en la instalación de sistemas de energía solar fotovoltaica para hogares y empresas. Nacimos con la misión de hacer la energía renovable accesible para todos.
                  </p>
                  <p>
                    Con más de 1,000 instalaciones exitosas en toda España, nos hemos consolidado como un referente en el sector de la energía solar. Nuestro equipo de profesionales certificados cuenta con años de experiencia en el diseño e instalación de sistemas fotovoltaicos de alta eficiencia.
                  </p>
                  <p>
                    En Nubia Solar no solo instalamos paneles solares, creamos soluciones energéticas personalizadas que se adaptan perfectamente a las necesidades de cada cliente, maximizando el ahorro y minimizando el impacto ambiental.
                  </p>
                </div>
              </div>
              <div>
                <img 
                  src="https://images.pexels.com/photos/8853504/pexels-photo-8853504.jpeg" 
                  alt="Equipo Nubia Solar" 
                  className="rounded-2xl nubia-shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="nubia-shadow border-0 bg-white">
                <CardHeader>
                  <div className="w-16 h-16 bg-nubia-gold-soft/20 rounded-2xl flex items-center justify-center mb-4">
                    <Target className="w-8 h-8 text-nubia-gold-primary" />
                  </div>
                  <CardTitle className="text-3xl text-nubia-green-primary">Nuestra Misión</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg text-nubia-text-dark/70 leading-relaxed">
                    Transformar el panorama energético español impulsando la adopción de energía solar, 
                    ofreciendo soluciones accesibles, eficientes y de alta calidad que permitan a hogares 
                    y empresas reducir sus costes energéticos mientras contribuyen a un planeta más sostenible.
                  </p>
                </CardContent>
              </Card>

              <Card className="nubia-shadow border-0 bg-white">
                <CardHeader>
                  <div className="w-16 h-16 bg-nubia-green-support/20 rounded-2xl flex items-center justify-center mb-4">
                    <Eye className="w-8 h-8 text-nubia-green-support" />
                  </div>
                  <CardTitle className="text-3xl text-nubia-green-primary">Nuestra Visión</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg text-nubia-text-dark/70 leading-relaxed">
                    Ser la empresa de referencia en energía solar en España, reconocida por nuestra 
                    excelencia técnica, compromiso con el cliente y contribución al desarrollo sostenible. 
                    Aspiramos a que cada hogar y empresa pueda generar su propia energía limpia y económica.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-4">
              Nuestros Valores
            </h2>
            <p className="text-xl text-nubia-text-dark/70 max-w-3xl mx-auto">
              Los principios que guían cada decisión y acción en Nubia Solar
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-2 border-nubia-gray-light hover:border-nubia-gold-primary/30 transition-all">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-nubia-gold-soft/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-7 h-7 text-nubia-gold-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-2xl text-nubia-green-primary mb-3">{value.title}</CardTitle>
                        <p className="text-nubia-text-dark/70 leading-relaxed">{value.description}</p>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 nubia-gradient text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-nubia-gold-primary mb-2">1000+</div>
                <div className="text-lg">Instalaciones Completadas</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-nubia-gold-primary mb-2">25</div>
                <div className="text-lg">Años de Garantía</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-nubia-gold-primary mb-2">70%</div>
                <div className="text-lg">Ahorro Medio</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-nubia-gold-primary mb-2">100%</div>
                <div className="text-lg">Satisfacción Cliente</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-nubia-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-6">
            ¿Quieres Conocernos Mejor?
          </h2>
          <p className="text-xl text-nubia-text-dark/70 mb-10 max-w-3xl mx-auto">
            Ponte en contacto con nosotros y descubre cómo podemos ayudarte a transformar tu energía
          </p>
          <Link to="/contacto">
            <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-7 text-lg shadow-xl">
              Contactar con Nosotros
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Empresa;